import type { BookingConfirmationSummary } from '@massilia/queries'

import { Button } from '@massilia/ui'
import { Link } from '@tanstack/react-router'

import { CarRentalsSection } from '@/components/booking-sections/car-rentals-section'
import { ExtrasSection } from '@/components/booking-sections/extras-section'
import { HotelsSection } from '@/components/booking-sections/hotels-section'
import { InsuranceSection } from '@/components/booking-sections/insurance-section'
import { SpecificitySection } from '@/components/specificity/specificity-section'

import type { PaymentConfirmationBookingData } from './payment-confirmation-booking-data'

import { CompleteYourTripSection } from './complete-your-trip-section'
import { PaymentConfirmationLayout } from './payment-confirmation-layout'
import { PaymentConfirmationSharedScreen } from './payment-confirmation-shared-screen'
import { paymentConfirmationVariants } from './payment-confirmation-variants'
import { SummaryRow } from './summary-row'
import { TripRecapSection } from './trip-recap-section'

interface PaymentFailedProps {
  sessionId?: string
  axeptaDetails?: Record<string, string>
  booking: PaymentConfirmationBookingData
  summary?: BookingConfirmationSummary
}

export function PaymentFailedComponent({
  sessionId,
  axeptaDetails,
  booking,
  summary,
}: PaymentFailedProps) {
  const variant = paymentConfirmationVariants.failed

  return (
    <PaymentConfirmationLayout variant={variant}>
      <div className="flex flex-col gap-3 rounded-2xl border border-gray-light-200 bg-white p-6">
        <div className="text-2xl font-semibold text-gray-light-900">{variant.title}</div>
        <p className="text-sm text-gray-light-700">{variant.description}</p>

        <SummaryRow label="Numéro de dossier" value={booking.bookingRef} />
        <SummaryRow label="Adresse email" value={booking.customerEmail} />
        <SummaryRow label="Téléphone" value={booking.customerPhone} />
        {booking.depositDeadlineLabel && (
          <SummaryRow
            label="Date limite de réception du virement"
            value={booking.depositDeadlineLabel}
          />
        )}

        <div className="mt-2 flex flex-wrap gap-3">
          <Link to="/connexion">
            <Button type="button">Accéder à ma réservation</Button>
          </Link>
          {sessionId && (
            <Button asChild type="button" variant="outline">
              <a href={`/paiement?session=${encodeURIComponent(sessionId)}`}>
                Réessayer le paiement
              </a>
            </Button>
          )}
        </div>

        {(axeptaDetails?.Code || axeptaDetails?.Description || axeptaDetails?.Status) && (
          <div className="bg-error-50 mt-2 flex flex-col gap-1 rounded-lg p-4">
            <div className="text-xs font-semibold text-error-600">Détails techniques</div>
            {axeptaDetails?.Code && <DetailRow label="Code" value={axeptaDetails.Code} />}
            {axeptaDetails?.Description && (
              <DetailRow label="Description" value={axeptaDetails.Description} />
            )}
            {axeptaDetails?.Status && <DetailRow label="Statut" value={axeptaDetails.Status} />}
            {(axeptaDetails?.PayID || axeptaDetails?.TransID) && (
              <DetailRow
                label="Référence"
                value={axeptaDetails.PayID ?? axeptaDetails.TransID ?? ''}
              />
            )}
          </div>
        )}
      </div>

      <TripRecapSection summary={summary} />

      {summary?.insurance && <InsuranceSection insurance={summary.insurance} />}
      {summary?.hotels && summary.hotels.length > 0 && <HotelsSection hotels={summary.hotels} />}
      {summary?.carRentals && summary.carRentals.length > 0 && (
        <CarRentalsSection carRentals={summary.carRentals} />
      )}
      {summary?.extras && summary.extras.length > 0 && <ExtrasSection extras={summary.extras} />}

      <CompleteYourTripSection
        bookingId={summary?.bookingId}
        hasInsurance={!!summary?.insurance}
        sessionId={sessionId}
        summary={summary}
      />

      {sessionId && <SpecificitySection sessionId={sessionId} />}

      <PaymentConfirmationSharedScreen isReunionAgency={summary?.agency === 'RUN'} />
    </PaymentConfirmationLayout>
  )
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="text-error-700 text-xs">
      {label} : <span className="font-mono">{value}</span>
    </div>
  )
}
